/** @jsx jsx */
import { jsx, css } from "@emotion/core"
import { Autocomplete, Skeleton, Alert } from "@material-ui/lab"
import {
  ChangeEvent,
  useMemo,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react"
import { SelectorListItem } from "../UI/List/Selector/selectorTypes"
import { TextField, CircularProgress, Snackbar } from "@material-ui/core"
import LoadingStateWrapper from "../UI/LoadingStateWrapper"
import ErrorState from "../UI/ErrorState"
import { Theme } from "../types"
import { useCartUpdater, useCartState } from "../../context/CartContext"
import { getDiscountsOfUser } from "../Discount/discountService"
import AuthContext from "../../context/AuthContext"
import { useAllUsersQuery } from "../User/queries"

type Props = {
  onChange: (newValue: SelectorListItem | null) => void
}

function CartBuyerSelector({ onChange }: Props) {
  const { users, isUsersLoading, usersErrorState } = useAllUsersQuery()
  const { updateCartDiscount, setBuyer, clearCart } = useCartUpdater()
  const { buyerId } = useCartState()

  const [progress, setProgress] = useState<"loading" | "ideal" | "error">(
    "ideal"
  )
  const auth = useContext(AuthContext)

  const [selected, setSelected] = useState<SelectorListItem | null>(null)

  const handleOnChange = useCallback(
    (_event: ChangeEvent<{}> | null, newValue: SelectorListItem | null) => {
      if (newValue) {
        setProgress("loading")
        setBuyer(newValue.id)
        getDiscountsOfUser(newValue.id, auth?.getAccessToken())
          .then((response) => {
            updateCartDiscount(response)
            onChange(newValue)
            setProgress("ideal")
          })
          .catch((err) => {
            setProgress("error")
          })
      } else {
        updateCartDiscount({})
        onChange(newValue)
        setProgress("ideal")
        setBuyer(null)
      }
      setSelected(newValue)
    },
    [auth, onChange, setBuyer, updateCartDiscount]
  )

  useEffect(() => {
    if (buyerId && users?.length && selected === null) {
      const buyer = users.find((u) => u[1] === buyerId)
      if (buyer) {
        const listItem = { id: buyer[1], label: buyer[0] }
        setSelected(listItem)
        handleOnChange(null, listItem)
      } else {
        //if it can't find the selected user, the order invalid.
        //Probably logged in with other account, or the user has been deleted meanwhile
        clearCart()
      }
    }
  }, [buyerId, users, clearCart, handleOnChange, selected])

  const handleErrorClose = () => {
    setProgress("ideal")
  }

  const usersOptions: SelectorListItem[] = useMemo(
    () =>
      users
        ? users.map((u) => ({
            id: u[1],
            label: u[0],
          }))
        : [],
    [users]
  )

  return (
    <LoadingStateWrapper
      isLoading={isUsersLoading}
      errorState={usersErrorState}
      loadingSkeleton={
        <Skeleton variant="rect" height={55} css={styles.root} />
      }
      errorStateComponent={
        <ErrorState
          state={usersErrorState}
          errorText={"Nem lehetett betölteni a vevőket!"}
          retyingText={"Hiba lépett fel a vevők betöltésekor!"}
        />
      }
    >
      <div css={styles.selectorContainer}>
        {progress === "loading" && (
          <CircularProgress size={36} css={styles.progress} />
        )}
        <Autocomplete
          options={usersOptions}
          value={selected}
          getOptionLabel={(option) => option.label}
          renderInput={(params) => (
            <TextField
              {...params}
              error={progress === "error"}
              label="Vevő"
              variant="outlined"
            />
          )}
          blurOnSelect
          autoHighlight
          onChange={handleOnChange}
          disabled={progress === "loading"}
          css={styles.root}
          data-testid="selector_buyer"
        />
      </div>
      <Snackbar
        open={progress === "error"}
        autoHideDuration={6000}
        onClose={handleErrorClose}
      >
        <Alert
          severity="error"
          elevation={6}
          variant="filled"
          onClose={handleErrorClose}
        >
          Nem sikerült betölteni a felhasználót
        </Alert>
      </Snackbar>
    </LoadingStateWrapper>
  )
}

export default CartBuyerSelector

const styles = {
  root: (theme: Theme) => css`
    margin-bottom: ${theme.spaces.smallScale.l};
  `,
  selectorContainer: css`
    position: relative;
  `,
  progress: (theme: Theme) => css`
    color: ${theme.palette.success.main};
    position: absolute;
    top: 10%;
    left: calc(50% - 13px);
    z-index: 1;
  `,
}
